import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';

/**
 * phase2-interfaces.md §2.5 — public feature-flag discovery endpoint.
 *
 * The web app calls this once at boot to decide whether to render the
 * Phase 2 kickoff-aware flow or the legacy EmptyState. Cached for 30s on
 * the server (max-age=30); we mirror that on the client by setting React
 * Query's `staleTime` to 30s. UI components that toggle on impersonation
 * change can call `queryClient.invalidateQueries(['health', 'config'])`
 * to force a refetch.
 *
 * Decision Q1 — endpoint requires NO auth, so this hook works pre-Clerk.
 */
export type Phase2FlagValue = 'off' | 'admin_only' | 'on';

export interface HealthConfig {
  phase2: Phase2FlagValue;
  buildSha: string | null;
}

export const HEALTH_CONFIG_QUERY_KEY = ['health', 'config'] as const;

export function useFeatureFlags() {
  return useQuery<HealthConfig>({
    queryKey: HEALTH_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<HealthConfig>('/api/v1/health/config');
      if (!res.success || !res.data) {
        // Default to "off" on transport failure — fail closed to the legacy
        // path. The contract is explicit that the legacy code stays in-tree
        // through the rollout.
        return { phase2: 'off', buildSha: null };
      }
      return res.data as HealthConfig;
    },
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Mirrors `isPhase2Active` on the API side. The UI uses this predicate at
 * exactly one place (the `/start` flow) — keeping the rule local makes the
 * "Phase 2 vs legacy" branch readable.
 */
export function isPhase2Active(
  flag: Phase2FlagValue | undefined,
  isImpersonating: boolean
): boolean {
  if (flag === 'on') return true;
  if (flag === 'admin_only') return isImpersonating;
  return false;
}
